<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { get } from 'svelte/store';
    import { _ } from 'svelte-i18n';
    import { onMount } from 'svelte';

    import { MapPointLocation } from '../../../utilities/MapPointLocation';
    import { onMapReady } from '../../../utilities/onMapReady';
    import ErrorInline from '../../../shared/ErrorInline.svelte';
    import Checkbox from '../../../shared/Checkbox/Checkbox.svelte';
    import { debug, routes } from '../../../../stores/store';
    import { JobsOnMap } from '../JobsOnMap.js';
    import { RouteOnMap } from '../RouteOnMap';

    import './JobOne.css';
    import { captureMessage } from '../../../utilities/Sentry';
    import type { RouteResponseType } from '../Routes/RoutesApi';
    import { RouteOne } from '../Routes/RouteOne';
    import Duration from '../../../shared/Duration.svelte';
    import Distance from '../../../shared/Distance.svelte';
    import RouteSummaryByType from '../RouteSummaryByType.svelte';
    import RouteSummaryByCostCategory from '../RouteSummaryByCostCategory.svelte';
    import { ConnectionApi } from '../ConnectionApi';
    import { api, clientSettings } from '../../../../stores/api';
    import Monetary from '../../../shared/Monetary.svelte';
    import IconRouteOneWay from '../../../shared/Icons/IconRouteOneWay.svelte';
    import IconRouteModified from '../../../shared/Icons/IconRouteModified.svelte';
    import ConnectionNote from '../ConnectionNote.svelte';
    import { getHumanFriendlyDateDifferenceForToday } from '../../../utilities/datetime';
    import { canWrite } from '../../../shared/Permissions/writeAccess';
    import { z } from 'zod';

    export let route: RouteResponseType;
    export let showHeader: boolean = false;
    export let switchable: boolean = true;
    export let closeButton: boolean = false;
    export let simpleView: boolean = false;
    export let index: number;
    export let expandedIndex: number = -1;
    export let jobsOnMap: JobsOnMap;
    export let routeOnMap: RouteOnMap;
    export let fullEditableForm: boolean = false;
    export let enableMap: boolean = true;
    export let enableCheckbox: boolean = true;
    export let locationTypesItems: { id: string; label: string }[] = [];
    export let showNote: boolean = true;

    let error = '';
    let dispatch = createEventDispatcher();
    let checked: boolean = false;

    let routeOne: RouteOne = new RouteOne();
    routeOne.set(route);

    function toggle() {
        if (switchable) {
            // https://er.automapa.pl/issues/41683
            expandedIndex = expandedIndex === index ? -1 : index;
            if (expandedIndex !== index) {
                console.log('toggle, closing job ', expandedIndex, index);
                jobsOnMap && jobsOnMap.jobShowBalloon(routeOne.getId(), false);
                closePointOnMap();
                dispatch('close');
                let connectionId = routeOne.getCalculationResult().getConnectionId();
                if (connectionId && routeOnMap) {
                    routeOnMap.routeRemove(connectionId, routeOne.getCalculationResult().getRouteTypeId());
                }
            } else {
                console.log('toggle, opening job ', expandedIndex, index);
                // TODO: add zooming to both points (from-to)
                if (routeOne.getFrom().hasCoords()) {
                    jobsOnMap && jobsOnMap.zoomTo(routeOne.getFrom().getX(), routeOne.getFrom().getY());
                    // jobsOnMap && jobsOnMap.jobShowBalloon(route.location_from.id);
                }
                let connectionId = routeOne.getCalculationResult().getConnectionId();
                if (connectionId && routeOnMap) {
                    // FIXED: Cannot read properties of undefined (reading 'routeShow') - https://lupus.automapa.pl/organizations/automapa/issues/4347/?project=14
                    routeOnMap.routeShow(connectionId, routeOne.getCalculationResult().getRouteTypeId());
                }
                dispatch('open');
            }
        }
    }

    async function onUpdate() {
        closePointOnMap();
        console.log('jobRequest, before parse', routeOne.get());
    }

    let mapPointLocation: MapPointLocation | null;

    let startPointOnMap = function () {
        if (mapPointLocation) {
            if (!mapPointLocation.isCurrent()) {
                if (
                    mapPointLocation.start({
                        message: $_('Click on the map to set job #{count} coordinates', {
                            values: { count: routeOne.getFrom().getId() },
                        }),
                    })
                ) {
                    dispatch('pointonmapstart');
                }
            } else {
                mapPointLocation.close(true);
            }
        }
    };

    export const closePointOnMap = function () {
        if (mapPointLocation) {
            if (mapPointLocation.close(true)) {
                dispatch('pointonmapclose');
            }
        }
    };

    onMount(() => {
        Promise.all([onMapReady()]).then(response => {
            // console.log('locationTypesItems', locationTypesItems);
            mapPointLocation = new MapPointLocation();
            mapPointLocation.addEventListener('setCoordinates', function (evt: any) {
                routeOne.getFrom().setCoords(evt.detail.coords.x, evt.detail.coords.y);
                // route.location_from.geom = {
                //     type: 'Point',
                //     coordinates: [evt.detail.coords.x, evt.detail.coords.y],
                // };
                onUpdate().then(() => {
                    dispatch('setcoordinates');
                });
            });
            mapPointLocation.addEventListener('close', function () {
                dispatch('pointonmapclose');
            });
        });

        return () => {
            if (mapPointLocation) {
                mapPointLocation.close(true);
            } else {
                // captureMessage('captureMessage: mapPointLocation is not defined');
            }
        };
    });

    $: checked =
        ($routes.selectMode === 'filtered' && !$routes.selectedOrUnselected.includes(routeOne.getId())) ||
        ($routes.selectMode === 'selected' && $routes.selectedOrUnselected.includes(routeOne.getId()));

    $: {
        expandedIndex = expandedIndex;
        console.log('JobOneRow, expandedIndex', expandedIndex, index);
    }

    $: {
        routeOne.set(route);
        routeOne = routeOne;
    }
</script>

<div class="connection-element connection-element-data" class:simple-view={simpleView} class:checked>
    <div class="checkbox">
        {#if enableCheckbox}
            <!-- zaznaczenie odsłania pasek akcji z przeliczeniem trasy, czyli operacją zapisu -->
            <Checkbox
                disabled={!$canWrite}
                label=""
                {checked}
                onChange={() => {
                    if ($routes.selectedOrUnselected.includes(routeOne.getId())) {
                        $routes.selectedOrUnselected = $routes.selectedOrUnselected.filter(id => id !== routeOne.getId());
                    } else {
                        $routes.selectedOrUnselected = [...$routes.selectedOrUnselected, routeOne.getId()];
                    }
                }}
            />
        {/if}
    </div>

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="address-from" class:switchable class:extended-background={expandedIndex === index && false} on:click={toggle}>
        <div class="bold three-dots">{routeOne.getFrom().getCity()}</div>
        <div class="light three-dots">{routeOne.getFrom().getPostalCode()}</div>
    </div>

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="name-from" class:switchable class:extended-background={expandedIndex === index} on:click={toggle}>
        <div class="bold three-dots">{routeOne.getFrom().getExtId()}</div>
        <div class="light three-dots">
            {routeOne.getFrom().getFullStreet()}
        </div>
    </div>

    <div class="icons" class:extended-background={expandedIndex === index}>
        <div style="display: flex; flex-direction: column; row-gap: 0px; justify-content: center; align-items: center;">
            <IconRouteOneWay scale={0.6} />
            {#if routeOne.isModified()}
                <IconRouteModified scale={0.5} />
            {/if}
        </div>
    </div>

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="name-to" class:switchable class:extended-background={expandedIndex === index} on:click={toggle}>
        <div class="bold three-dots" style="width: 100%;">{routeOne.getTo().getExtId()}</div>
        <div class="light three-dots" style="width: 100%;">{routeOne.getTo().getFullStreet()}</div>
    </div>

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="address-to" class:switchable class:extended-background={expandedIndex === index && false} on:click={toggle}>
        <div class="bold three-dots" style="width: 100%;">{routeOne.getTo().getCity()}</div>
        <div class="light three-dots" style="width: 100%;">{routeOne.getTo().getPostalCode()}</div>
    </div>

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="route-category" class:switchable class:extended-background={expandedIndex === index && false} on:click={toggle}>
        <div class="light">{routeOne.getRouteTypeName()}</div>
    </div>

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="route-length" class:switchable class:extended-background={expandedIndex === index && false} on:click={toggle}>
        <!--{routeOne.getCalculationResult().getRouteMeters()}-->
        <Distance meters={routeOne.getCalculationResult().getRouteMeters()} />
    </div>

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="route-time" class:switchable class:extended-background={expandedIndex === index && false} on:click={toggle}>
        <!--{routeOne.getCalculationResult().getTotalTimeSeconds()}-->
        <Duration seconds={routeOne.getCalculationResult().getTotalTimeSeconds()} />
    </div>

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="route-cost" class:switchable class:extended-background={expandedIndex === index && false} on:click={toggle}>
        <div class="bold">
            <Monetary value={routeOne.getCalculationResult().getEtollPrice()} currency="" />
        </div>
    </div>

    {#if expandedIndex === index}
        <div class="expanded">
            <div
                style="display: flex; flex-direction: row; justify-content: space-between; width: 100%; padding: 20px; column-gap: 20px; row-gap: 20px; flex-flow: wrap;"
            >
                {#if showNote}
                    <div>
                        <div>{$_('Note')}</div>
                        <div>
                            <ConnectionNote {routeOne} />
                        </div>
                    </div>
                {/if}
                <div style="display: flex; flex-direction: column; justify-content: space-between; row-gap: 20px">
                    <div><RouteSummaryByType {routeOne} /></div>
                    <div><RouteSummaryByCostCategory {routeOne} /></div>
                </div>
            </div>
            {#if $debug}
                <div>
                    <div>getConnection:{routeOne.getId()}</div>
                    <div>unique_id:{routeOne.get().unique_id}</div>
                    <div>
                        getCalculation:{routeOne.getCalculation().exists() ? routeOne.getCalculation().getId() : 'null'}
                    </div>
                    <div>
                        getCalculationResult:{routeOne.getCalculationResult().exists() ? routeOne.getCalculationResult().getId() : 'null'}
                    </div>
                </div>
            {/if}
        </div>
    {/if}
    <div class="calc-date" class:calc-date-warning={routeOne.isOutdated()} title="Data obliczenia trasy">
        <!-- make sure routeOne.getCalculation().getDay() is in format 'YYYY-MM-DD' -->
        {#if z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .safeParse(routeOne.getCalculation().getDay()).success}
            {#if $clientSettings.display.showRelativeToday}
                {getHumanFriendlyDateDifferenceForToday(routeOne.getCalculation().getDay())}
            {:else}
                {routeOne.getCalculation().getDay()}
            {/if}
        {:else}
            ---
        {/if}
    </div>
</div>

<style>
    .city {
        color: #000;
    }
    .street {
        color: #888;
    }

    :global(.job-list-overlay) .checkbox {
        display: none;
    }
    :global(.job-list-overlay) .order-element-data {
        border-bottom: unset;
    }
    .connection-element {
        position: relative;
        min-height: 35px;
    }
    .calc-date {
        position: absolute;
        bottom: 0;
        right: 0;
        background-color: #dedede;
        padding: 0 6px 0 6px;
        font-size: 11px;
        color: #0009;
    }
    .calc-date-warning {
        background-color: var(--automapa-red);
        color: #ffffff;
        border-top-left-radius: 3px;
    }
    .three-dots {
        flex: 0 1 auto;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
</style>
